import React, { useRef, useState } from 'react';
import {
  FormInstance,
  Upload,
  Button,
  UploadFile,
  UploadProps,
  Alert,
  App,
  Form,
} from 'antd';
import clsx from 'clsx';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import ImgCrop, { ImgCropProps } from 'antd-img-crop';
import { getMessageInError } from '../../../hooks/fetch';
import { getBase64 } from '../../../utils';
import { useUploadFile } from '../../../hooks/file';
import { useMessageError } from '../../../hooks/common';

import styles from './index.module.scss';
import { IconUploadImage } from '../Icon';
import { FileData } from '../../../store/types';

export const getFullFileUrl = (url: string | undefined) =>
  url ? `${process.env.REACT_APP_AMAZON_S3_URL}/${url}` : '';

export const generateFileObjFromUrl = (url: string): UploadFile => ({
  url,
  uid: url,
  name: url.replace('images/', ''),
  status: 'done',
});

interface InputCodeWrapperProps extends React.PropsWithChildren {
  name: string;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  required?: boolean;
  form?: FormInstance;
  accept?: string;
  uploadUrl?: string;
  multiple?: boolean;
  className?: string;
  cropperProps?: Partial<ImgCropProps>;
  wide?: boolean;
  handleFileId?: (fotoId: string, res: FileData) => void;
}

function ImageUpload({
  name,
  fileList,
  setFileList,
  required = false,
  accept = '.jpg,.png,.jpeg',
  uploadUrl = 'files/upload',
  multiple = false,
  className,
  cropperProps,
  wide = false,
  handleFileId,
}: InputCodeWrapperProps) {
  const { message } = App.useApp();
  const uploadRef = useRef<HTMLButtonElement | null>(null);
  const uploader = useUploadFile(uploadUrl);

  useMessageError([uploader]);

  const cropperParams: Partial<ImgCropProps> = {
    rotationSlider: true,
    showReset: true,
    resetText: 'Сбросить',
    modalTitle: 'Редактировать фото',
    aspect: wide ? 16 / 9 : undefined,
    ...cropperProps,
  };

  const onPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = (await getBase64(file.originFileObj)) as string;
    }
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(
        `<img src="${file.url || file.preview || ''}" style="max-width:100%" />`
      );
    }
  };

  const onRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const [replacingIndex, setReplacingIndex] = useState<number | null>(null);

  const beforeUpload = async (file: RcFile, _?: RcFile[], index?: number) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      message.error('You can only upload JPG/PNG file!');
      return false;
    }

    if (!(file.size / 1024 / 1024 < 50)) {
      message.error('Image must be smaller than 50MB!');
      return false;
    }

    const newFile: UploadFile = {
      originFileObj: file,
      uid: file.uid,
      name: file.name,
      status: 'done',
    };

    newFile.preview = (await getBase64(file)) as string;

    if (index !== null && index !== undefined) {
      const newFileList = [...fileList];
      newFileList[index] = newFile;
      setFileList(newFileList);
    } else if (multiple) {
      setFileList([...fileList, newFile]);
    } else {
      setFileList([newFile]);
    }

    const formData = new FormData();
    formData.append('file', file);

    uploader.fetch(formData).then((res) => {
      if (res?.url) {
        setFileList((prevFileList) =>
          prevFileList.map((item) =>
            item.uid === file.uid ? { ...item, url: res.url } : item
          )
        );
      }
      if (res?.id) {
        handleFileId?.(res.id, res as FileData);
      }
    });

    return false;
  };

  const props: UploadProps = {
    multiple,
    showUploadList: false,
    name,
    accept,
    onRemove,
    beforeUpload,
    fileList,
    listType: 'picture-card',
  };

  return (
    <div key={fileList.length}>
      <div
        className={clsx(styles.uploader, className, {
          [styles.singleUpload]: !multiple,
        })}
      >
        {fileList.length > 0 && (
          <>
            {fileList.map((file) => (
              <div
                key={file.uid}
                className={clsx(styles.imageContainer, styles.overlay)}
                tabIndex={-1}
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(file);
                }}
              >
                <div className={styles.contentInOverlay}>
                  <CloseCircleOutlined
                    className={styles.btn}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(file);
                    }}
                    title="Delete image"
                  />
                </div>
                <img
                  className={styles.uploadedImg}
                  src={
                    file.preview || getFullFileUrl(file.url) || file.thumbUrl
                  }
                  alt={file.name}
                />
              </div>
            ))}
          </>
        )}

        <Form.Item
          name={name}
          rules={
            required
              ? [{ required: true, message: 'This field is required' }]
              : undefined
          }
          className={
            fileList.length > 0 ? 'no-space-form-item' : 'no-space-form'
          }
        >
          <Upload
            {...props}
            className={fileList.length > 0 ? styles.upload : styles.uploadDrag}
          >
            {fileList.length > 0 && !multiple ? null : (
              <>
                {fileList.length > 0 ? (
                  <div className={styles.uploadBtn}>
                    <PlusOutlined /> Upload
                  </div>
                ) : (
                  <div className={styles.dragBtn}>
                    <IconUploadImage />
                    <span>Drag drop photo here, or click to select files</span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 300,
                        lineHeight: '100%',
                      }}
                    >
                      up to 2MB recommended
                    </span>
                  </div>
                )}
              </>
            )}
          </Upload>
        </Form.Item>

        <ImgCrop {...cropperParams}>
          <Upload
            name="upload-for-replace"
            className={styles.hiddenUpload}
            beforeUpload={(file) => {
              if (replacingIndex !== null && replacingIndex !== undefined) {
                beforeUpload(file, undefined, replacingIndex);
                setReplacingIndex(null);
              }
              return false;
            }}
          >
            <Button ref={uploadRef}>Replace</Button>
          </Upload>
        </ImgCrop>
      </div>

      {uploader.error ? (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          message={getMessageInError(uploader.error)}
          closable
          onClose={uploader.clearError}
        />
      ) : null}
    </div>
  );
}

export default ImageUpload;
